import SubServicePageLayout from "@/components/SubServicePageLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { resolveIcon } from "@/lib/iconResolver";

const fallback = {
  seoTitle: "SEO Services That Drive Sustainable, High-Intent Growth",
  seoDescription: "We build SEO systems focused on visibility, intent capture, and long-term compounding traffic. Not vanity rankings. Technical SEO, content strategy, and authority building.",
  canonicalUrl: "https://xperfomax.com/seo",
  category: "services",
  categoryLabel: "Services / SEO",
  headline: "SEO Services That Drive Sustainable, High-Intent Growth",
  subheadline: "We build SEO systems focused on visibility, intent capture, and long-term compounding traffic. Not vanity rankings.",
  ctaText: "Start a serious conversation",
  problemTitle: "Why Most SEO Efforts Fail",
  problems: [
    "Over-focus on keywords, not intent.",
    "No integration with conversion strategy.",
    "Technical SEO ignored until it's too late.",
    "Reporting without business context.",
  ],
  systemTitle: "Our SEO System",
  systemDescription: "SEO only works when technical, content, and authority systems work together.",
  services: [
    { name: "Technical SEO & Site Audits", icon: "Settings", components: ["Crawlability & indexing optimization", "Core Web Vitals improvement", "Site architecture analysis"], outcome: "Search engines can understand, trust, and scale your site." },
    { name: "On-Page SEO & Content Optimization", icon: "FileText", components: ["Keyword & intent mapping", "Content gap analysis", "Meta & heading optimization"], outcome: "Traffic that aligns with buyer readiness, not just volume." },
    { name: "Off-Page SEO & Authority Building", icon: "Link", components: ["Sustainable link building", "Brand mentions & PR", "Digital PR campaigns"], outcome: "Search visibility that sustains algorithm changes." },
    { name: "Content Strategy & Topic Clustering", icon: "Search", components: ["Topic cluster architecture", "Content calendar development", "Pillar page strategy"], outcome: "Topical authority that compounds over time." },
    { name: "Local & Geo-Targeted SEO", icon: "MapPin", components: ["Google Business Profile optimization", "Local citation building", "Multi-location SEO strategy"], outcome: "Dominate local search for high-intent queries." },
    { name: "SEO Performance Reporting", icon: "BarChart3", components: ["Ranking & traffic dashboards", "Revenue attribution", "Competitor benchmarking"], outcome: "SEO decisions tied to growth, not assumptions." },
  ],
  engagementShape: {
    title: "How SEO engagement unfolds.",
    description: "No fixed timelines. No deliverables lists. Structure that adapts to your content and competitive landscape.",
  },
  processTitle: "From audit to authority.",
  processSteps: [
    { number: "01", title: "SEO & Technical Audit", description: "Deep analysis of your current search presence, technical foundation, and competitive landscape." },
    { number: "02", title: "Keyword & Intent Mapping", description: "Identify high-value keywords aligned with buyer intent and business objectives." },
    { number: "03", title: "Content & On-Page Optimization", description: "Optimize existing content and develop new assets based on topic cluster strategy." },
    { number: "04", title: "Authority & Link Strategy", description: "Build sustainable authority through quality content, digital PR, and earned links." },
    { number: "05", title: "Monitoring & Continuous Improvement", description: "Track performance, refine strategy, and capitalize on emerging opportunities." },
  ],
  timelinePhases: [
    { number: "01", title: "SEO & Technical Audit", description: "Deep analysis of your current search presence and technical foundation.", decision: "Where to focus first based on impact vs. effort.", output: "Prioritized roadmap with quick wins and strategic bets.", riskRemoved: "Wasted effort on low-impact optimizations." },
    { number: "02", title: "Keyword & Intent Mapping", description: "Identify high-value keywords aligned with buyer intent.", decision: "Which keywords drive revenue, not just traffic.", output: "Intent-mapped keyword strategy with content briefs.", riskRemoved: "Ranking for keywords that don't convert." },
    { number: "03", title: "Content & On-Page Optimization", description: "Optimize existing content and develop new assets.", decision: "What content to create, update, or retire.", output: "Optimized pages with measurable ranking improvements.", riskRemoved: "Content cannibalization and topic gaps." },
    { number: "04", title: "Authority Building", description: "Sustainable link acquisition through quality and relevance.", decision: "Which authority signals matter for your space.", output: "Growing domain authority and referral traffic.", riskRemoved: "Toxic links and algorithm vulnerability." },
    { number: "05", title: "Continuous Optimization", description: "Track, refine, and capitalize on opportunities.", decision: "Where to double down based on performance data.", output: "Compounding organic growth trajectory.", riskRemoved: "Stagnation and competitive displacement." },
  ],
  technologyCategories: [
    { title: "Research", icon: "Compass", items: ["Ahrefs", "SEMrush", "Moz", "Keyword Planner"] },
    { title: "Technical", icon: "Wrench", items: ["Screaming Frog", "Sitebulb", "PageSpeed Insights"] },
    { title: "Analytics", icon: "LineChart", items: ["Google Search Console", "GA4", "Looker Studio"] },
    { title: "Content", icon: "PenTool", items: ["Clearscope", "Surfer SEO", "MarketMuse"] },
  ],
  techTitle: "SEO Tools & Technologies",
  techDescription: "Enterprise-grade tools for research, analysis, and optimization.",
  qualification: {
    forYou: [
      "You're serious about organic growth as a long-term strategy",
      "You have patience for compounding results over quick wins",
      "You understand SEO is an investment, not an expense",
      "You're willing to invest in quality content and technical improvements",
    ],
    notForYou: [
      "You're looking for overnight ranking improvements",
      "You want to game the system with shortcuts",
      "You're not willing to create or improve content",
      "You expect guaranteed #1 rankings",
    ],
  },
  faqs: [
    { question: "How long does SEO take to show results?", answer: "SEO is a long-term investment. Most clients see measurable improvements in 3-6 months, with significant results at 6-12 months. The timeline depends on your starting position, competition, and resources committed." },
    { question: "Do you guarantee rankings?", answer: "No ethical SEO provider guarantees specific rankings. Search algorithms are complex and constantly evolving. We guarantee a systematic, best-practice approach that consistently delivers results over time." },
    { question: "How do you measure SEO ROI?", answer: "We track organic traffic, keyword rankings, and conversions. But ultimately, we measure success by revenue attributed to organic search and the reduction in customer acquisition cost over time." },
    { question: "What's included in a technical SEO audit?", answer: "Our audits cover crawlability, indexing, site architecture, page speed, mobile experience, structured data, and content quality. We prioritize findings by business impact." },
    { question: "How does SEO integrate with paid marketing?", answer: "SEO and paid work together. Organic insights inform paid targeting, paid data reveals converting keywords, and together they reduce overall CAC while increasing market coverage." },
  ],
  finalCtaTitle: "Let's build an SEO system that compounds.",
  finalCtaDescription: "Organic growth is the most sustainable competitive advantage. Let's discuss how we can build yours.",
  finalCtaButtonText: "Start a serious conversation",
};

const SEO = () => {
  const { content } = usePageContent("seo", fallback);

  const services = content.services.map((s: any) => ({ ...s, icon: resolveIcon(s.icon) }));
  const technologyCategories = content.technologyCategories.map((t: any) => ({ ...t, icon: resolveIcon(t.icon) }));

  return (
    <SubServicePageLayout
      seoTitle={content.seoTitle}
      seoDescription={content.seoDescription}
      canonicalUrl={content.canonicalUrl}
      category={content.category}
      categoryLabel={content.categoryLabel}
      headline={content.headline}
      subheadline={content.subheadline}
      ctaText={content.ctaText}
      problemTitle={content.problemTitle}
      problems={content.problems}
      systemTitle={content.systemTitle}
      systemDescription={content.systemDescription}
      services={services}
      engagementShape={content.engagementShape}
      processTitle={content.processTitle}
      processSteps={content.processSteps}
      timelinePhases={content.timelinePhases}
      technologyCategories={technologyCategories}
      techTitle={content.techTitle}
      techDescription={content.techDescription}
      qualification={content.qualification}
      faqs={content.faqs}
      finalCtaTitle={content.finalCtaTitle}
      finalCtaDescription={content.finalCtaDescription}
      finalCtaButtonText={content.finalCtaButtonText}
    />
  );
};

export default SEO;
